import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { PaymentProvider } from './context/PaymentContext';
import { useTheme } from './context/ThemeContext';
import './App.css';

// Loading component with better UX
const LoadingSpinner = () => {
  const { isDarkMode, toggleTheme } = useTheme();

  return (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 dark:from-dark to-gray-100 dark:to-gray-800">
    <div className="text-center">
      <div className="relative mb-4">
        <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-[#ff9702] mx-auto"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <img 
            src={isDarkMode ? "/silkspace_logo_white.png" : "/silkspace_gif.gif"} 
            alt="Loading"
            className="w-16 h-16 object-contain"
            style={{ transform: 'none' }}
          />
        </div>
      </div>
      <p className="mt-4 text-gray-600 dark:text-gray-400 font-medium text-lg">Loading...</p>
    </div>
  </div>
  );
};

// Route-based code splitting with React.lazy
const Home = lazy(() => import('./pages/Home'));
const BookDetails = lazy(() => import('./pages/BookDetails'));
const MusicDetails = lazy(() => import('./pages/MusicDetails'));
const ArtistPage = lazy(() => import('./pages/ArtistPage'));
const EventDetails = lazy(() => import('./pages/EventDetails'));
const Auth = lazy(() => import('./pages/Auth'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ReaderDashboard = lazy(() => import('./pages/ReaderDashboard'));
const MyCollections = lazy(() => import('./pages/MyCollections'));
const AuthorDashboard = lazy(() => import('./pages/AuthorDashboard'));
const BookUpload = lazy(() => import('./pages/BookUpload'));
const Checkout = lazy(() => import('./pages/Checkout'));
const BookReader = lazy(() => import('./components/BookReader'));
const NewTitles = lazy(() => import('./pages/NewTitles'));
const ViewAllMusic = lazy(() => import('./pages/ViewAllMusic'));
const ViewAllCreme = lazy(() => import('./pages/ViewAllCreme'));
const ViewAllEvents = lazy(() => import('./pages/ViewAllEvents'));
const Recommended = lazy(() => import('./pages/Recommended'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const PaymentVerification = lazy(() => import('./pages/PaymentVerification'));
const BroadcastInstructions = lazy(() => import('./components/BroadcastInstructions'));
const ComingSoonBookDetail = lazy(() => import('./components/ComingSoonBookDetail'));

// Policy Pages
const Legal = lazy(() => import('./pages/Legal'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const SalesRefunds = lazy(() => import('./pages/SalesRefunds'));
const Sitemap = lazy(() => import('./pages/Sitemap'));

// Live Streaming Routes
const CreateLiveStream = lazy(() => import('./components/CreateLiveStream'));
const AuthorLiveStreams = lazy(() => import('./components/AuthorLiveStreams'));
const LiveStream = lazy(() => import('./components/LiveStream'));
const BroadcastStream = lazy(() => import('./components/BroadcastStream'));
const LiveStreamsBrowser = lazy(() => import('./components/LiveStreamsBrowser'));
const AuthorBroadcast = lazy(() => import('./components/livestream/AuthorBroadcast'));
const ChatInterface = lazy(() => import('./components/ChatInterface'));
const ChatRequests = lazy(() => import('./components/ChatRequests'));
const ChatDashboard = lazy(() => import('./components/ChatDashboard'));


function App() {
  useEffect(() => {
  // Force light theme on initial load
  const savedTheme = localStorage.getItem('darkMode');
  if (savedTheme === null) {
    // If no theme is saved, explicitly set to light
    localStorage.setItem('darkMode', 'false');
    document.documentElement.classList.remove('dark');
  }
}, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <PaymentProvider>
          <Router> 
            <div className="App min-h-screen bg-gray-50 dark:bg-dark transition-colors duration-300">
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  {/* Existing Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/book/:id" element={<BookDetails />} />
                  <Route path="/music/:id" element={<MusicDetails />} />
                  <Route path="/event/:id" element={<EventDetails />} />
                  <Route path="/read/:id" element={<BookReader />} />
                  <Route path="/artist/:id" element={<ArtistPage />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
<Route 
  path="/reader-dashboard" 
  element={
    <Suspense fallback={<LoadingSpinner />}>
      <ReaderDashboard />
    </Suspense>
  } 
/>                  <Route path="/creator-dashboard" element={<AuthorDashboard />} />
                  <Route path="/my-collection" element={<MyCollections />} />
                  <Route path="/upload-book" element={<BookUpload />} />
                  <Route path="/payment/verify" element={<PaymentVerification />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/new-titles" element={<NewTitles />} />
                  <Route path="/new-music" element={<ViewAllMusic />} />
                  <Route path="/creme-music" element={<ViewAllCreme />} />
                  <Route path="/events" element={<ViewAllEvents />} />
                  <Route path="/recommended" element={<Recommended />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/chat/:id" element={<ChatInterface />} />
                  <Route path="/chat/requests" element={<ChatRequests />} />
                  <Route path="/chats" element={<ChatDashboard />} />
                  
                  {/* Policy Routes */}
                  <Route path="/legal" element={<Legal />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-use" element={<TermsOfUse />} />
                  <Route path="/sales-refunds" element={<SalesRefunds />} />
                  <Route path="/sitemap" element={<Sitemap />} />
                  
                  {/* Live Streaming Routes */}
                  <Route path="/live-streams/create" element={<CreateLiveStream />} />
                  <Route path="/author/live-streams" element={<AuthorLiveStreams />} />
                  <Route path="/author/live-streams/:streamId" element={<AuthorLiveStreams />} />
                  <Route path="/live-stream/:streamId" element={<LiveStream />} />
                  <Route path="/live-stream/:streamId/broadcast" element={<BroadcastStream />} />
                  <Route path="/live-streams" element={<LiveStreamsBrowser />} />
                  <Route path="/live-now" element={<LiveStreamsBrowser />} />
                  <Route path="/live-stream/:streamId/broadcast-instructions" element={<BroadcastInstructions />} />
                  <Route path="/live-stream/:streamId/broadcast" element={<AuthorBroadcast />} />

                  <Route path="/coming-soon/:bookId" element={<ComingSoonBookDetail />} />

                  {/* 404 Route */}
                  <Route path="*" element={
                    <div className="min-h-screen flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-200 mb-4">404</h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">Page not found</p>
                        <a href="/" className="text-[#ff9702] hover:text-[#d18c13] font-semibold">
                          Return Home
                        </a>
                      </div>
                    </div>
                  } />
                </Routes>
              </Suspense>
            </div>
          </Router>
        </PaymentProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;